import { useCallback } from "react";
import { ErrorCode, LingoError } from "@thenounproject/lingo-core";
import useShowModal, { ModalTypes } from "@redux/actions/useModals";

type ErrorHandlerConfig = {
  onRetry?: () => void;
};

/**
 * Shared hook for handling download-specific error codes.
 * Handles identity verification, pending requests, and denied requests.
 *
 * @returns handleDownloadError - Returns true if the error was handled, false otherwise
 */
export function useDownloadErrorHandler() {
  const { showModal, dismissModal } = useShowModal();

  const handleDownloadError = useCallback(
    (error: LingoError, config?: ErrorHandlerConfig): boolean => {
      const { code, message } = error;

      switch (code) {
        case ErrorCode.downloadRequiresIdentity:
          showModal(ModalTypes.INFO_PROMPT, { callback: config?.onRetry });
          return true;

        case ErrorCode.downloadRequestPending:
          showModal(ModalTypes.CONFIRMATION, {
            title: "Request Pending",
            message,
            buttonText: "Continue",
            buttonProcessingText: "Continue",
            onConfirm: dismissModal,
          });
          return true;

        case ErrorCode.downloadRequiresVerifiedIdentity:
          showModal(ModalTypes.CONFIRMATION, {
            title: "Request Approved",
            message,
            buttonText: "Continue",
            buttonProcessingText: "Continue",
            onConfirm: dismissModal,
          });
          return true;

        case ErrorCode.downloadRequestDenied:
          showModal(ModalTypes.CONFIRMATION, {
            title: "Request Denied",
            message,
            buttonText: "Continue",
            buttonProcessingText: "Continue",
            onConfirm: dismissModal,
          });
          return true;

        default:
          return false;
      }
    },
    [showModal, dismissModal]
  );

  return { handleDownloadError };
}
